import { apiSlice } from './apiSlice';
import type { PlotPlan, FieldPlan, PlanOperation, BoundingBox, GlobalGrid, AllowedBoundingBox, StructuralObject, BedParams } from '../types/plan';
import { normalizePlanCells } from '../utils/planCells';
import { buildFieldPlanFromWorld, buildPlotPlanFromWorld } from '../utils/worldPlanAdapters';
import { loadSaveWorldSnapshot, toQueryFnError } from './saveWorldQuery';

/**
 * Adapt bounding box from API response to normalized format.
 * CN-0012: Simplified to use minRow/maxRow/minCol/maxCol directly.
 * Supports both camelCase and snake_case from backend.
 */
const adaptBoundingBox = (bbox: any): BoundingBox | null => {
  if (!bbox) return null;
  return {
    minRow: bbox.minRow ?? bbox.min_row ?? bbox.worldMin?.row ?? bbox.world_min?.row ?? 1,
    maxRow: bbox.maxRow ?? bbox.max_row ?? bbox.worldMax?.row ?? bbox.world_max?.row ?? 1,
    minCol: bbox.minCol ?? bbox.min_col ?? bbox.worldMin?.col ?? bbox.world_min?.col ?? 1,
    maxCol: bbox.maxCol ?? bbox.max_col ?? bbox.worldMax?.col ?? bbox.world_max?.col ?? 1,
    size: {
      height: bbox.size?.height ?? 1,
      width: bbox.size?.width ?? 1,
    },
  };
};

const adaptGlobalGrid = (grid: any): GlobalGrid => {
  if (!grid) return { size: 3072, maxBoundingBox: 1024 }; // Default для plots
  return {
    size: grid.size ?? 3072,
    maxBoundingBox: grid.maxBoundingBox ?? grid.max_bounding_box ?? 1024,
  };
};

/**
 * Adapt allowedBoundingBox from API response.
 * CN-0012: This defines the area where cells can be placed (global coordinates).
 */
const adaptAllowedBoundingBox = (bbox: any, globalGrid: GlobalGrid): AllowedBoundingBox => {
  if (bbox) {
    return {
      minRow: bbox.minRow ?? bbox.min_row ?? 1,
      maxRow: bbox.maxRow ?? bbox.max_row ?? globalGrid.maxBoundingBox,
      minCol: bbox.minCol ?? bbox.min_col ?? 1,
      maxCol: bbox.maxCol ?? bbox.max_col ?? globalGrid.maxBoundingBox,
    };
  }
  // Default: full grid starting at (1,1)
  return {
    minRow: 1,
    maxRow: globalGrid.maxBoundingBox,
    minCol: 1,
    maxCol: globalGrid.maxBoundingBox,
  };
};

const adaptStructuralObjects = (list: any[]): StructuralObject[] => {
  if (!Array.isArray(list)) return [];
  return list.map((so: any) => ({
    id: so.id,
    name: so.name ?? null,
    kind: so.kind,
    subtype: so.subtype,
    parameters: so.parameters ?? {},
  }));
};

const adaptBeds = (list: any[]): BedParams[] => {
  if (!Array.isArray(list)) return [];
  return list.map((b: any) => ({
    id: b.id,
    parameters: b.parameters ?? {},
  }));
};

export const planApi = apiSlice
  .enhanceEndpoints({ addTagTypes: ['SimTimeseries'] })
  .injectEndpoints({
  endpoints: (build) => ({
    getPlotPlan: build.query<PlotPlan, { saveId: string; plotId: string; fieldId?: string }>({
      queryFn: async ({ saveId, plotId }, api) => {
        try {
          const world = await loadSaveWorldSnapshot(api, saveId, {
            includeInactiveStructuralObjects: true,
          });
          const plan = buildPlotPlanFromWorld(world.structure, plotId);
          if (!plan) {
            return { error: { status: 404, data: 'Plot not found in world snapshot' } };
          }
          return { data: plan };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { saveId, plotId }) => [
        { type: 'Plan' as const, id: `plot-${plotId}` },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),
    patchPlotPlan: build.mutation<PlotPlan, { saveId: string; plotId: string; fieldId?: string; operations: PlanOperation[] }>({
      query: ({ saveId, plotId, operations }) => ({
        url: `/saves/${saveId}/plots/${plotId}/plan`,
        method: 'PATCH',
        body: { operations },
      }),
      transformResponse: (response: any) => {
        const globalGrid = adaptGlobalGrid(response?.globalGrid ?? response?.global_grid);
        return {
          plotId: response?.plotId ?? response?.plot_id ?? '',  // CN-0012: UUID string, не Number()
          boundingBox: adaptBoundingBox(response?.boundingBox ?? response?.bounding_box),
          allowedBoundingBox: adaptAllowedBoundingBox(response?.allowedBoundingBox ?? response?.allowed_bounding_box, globalGrid),
          globalGrid,
          cells: normalizePlanCells(response?.cells ?? []),
          structuralObjects: adaptStructuralObjects(response?.structuralObjects ?? response?.structural_objects ?? []),
          beds: adaptBeds(response?.beds ?? []),
        };
      },
      invalidatesTags: (result, error, { saveId, plotId, operations }) => [
        { type: 'Plan' as const, id: `plot-${plotId}` },
        { type: 'Plot' as const, id: String(plotId) },
        // getPlotPlan / getFieldPlan build the plan from the cached world
        // snapshot (see loadSaveWorldSnapshot). Any structural mutation must
        // invalidate it; otherwise navigating to a freshly-created bed/SO
        // shows "Ошибка: не удалось загрузить план участка" until hard reload.
        { type: 'SaveV2' as const, id: `world-${saveId}` },
        // Bed/SO parameter changes affect simulation covering context
        ...(operations.some(op => op.op === 'updateBedParameters' || op.op === 'updateStructuralObject')
          ? [{ type: 'SimTimeseries' as const }]
          : []),
      ],
    }),
    getFieldPlan: build.query<FieldPlan, { saveId: string; fieldId: string }>({
      queryFn: async ({ saveId, fieldId }, api) => {
        try {
          const world = await loadSaveWorldSnapshot(api, saveId);
          const plan = buildFieldPlanFromWorld(world.structure, fieldId);
          if (!plan) {
            return { error: { status: 404, data: 'Field not found in world snapshot' } };
          }
          return { data: plan };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { saveId, fieldId }) => [
        { type: 'Plan' as const, id: `field-${fieldId}` },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),
    patchFieldPlan: build.mutation<FieldPlan, { saveId: string; fieldId: string; operations: PlanOperation[] }>({
      query: ({ saveId, fieldId, operations }) => ({
        url: `/saves/${saveId}/fields/${fieldId}/plan`,
        method: 'PATCH',
        body: { operations },
      }),
      transformResponse: (response: any) => {
        const globalGrid: GlobalGrid = { size: 256, maxBoundingBox: 8 }; // Field constants (8x8 grid)
        return {
          fieldId: response?.fieldId ?? response?.field_id ?? '',  // CN-0012: UUID string, не Number()
          boundingBox: adaptBoundingBox(response?.boundingBox ?? response?.bounding_box),
          allowedBoundingBox: adaptAllowedBoundingBox(response?.allowedBoundingBox ?? response?.allowed_bounding_box, globalGrid),
          globalGrid,
          cells: normalizePlanCells(response?.cells ?? []),
          structuralObjects: adaptStructuralObjects(response?.structuralObjects ?? response?.structural_objects ?? []),
          beds: adaptBeds(response?.beds ?? []),
        };
      },
      invalidatesTags: (result, error, { saveId, fieldId, operations }) => [
        { type: 'Plan' as const, id: `field-${fieldId}` },
        // Field plan patches can create plots/beds/SOs. Plot plan reads go
        // through loadSaveWorldSnapshot, so we must invalidate the cached
        // world snapshot — otherwise navigating to a newly-created plot
        // shows "Ошибка: не удалось загрузить план участка".
        { type: 'SaveV2' as const, id: `world-${saveId}` },
        ...(operations.some(op => op.op === 'updateBedParameters' || op.op === 'updateStructuralObject')
          ? [{ type: 'SimTimeseries' as const }]
          : []),
      ],
    }),
  }),
});

export const {
  useGetPlotPlanQuery,
  useLazyGetPlotPlanQuery,
  usePatchPlotPlanMutation,
  useGetFieldPlanQuery,
  usePatchFieldPlanMutation,
} = planApi;
